import { isPassthroughComponent, type SimulateContext } from "./cable-current";
import { isJunction } from "./node-kind";
import { indexNodesById } from "./node-tree";
import {
  getActiveIntraLinkTargets,
  getEffectiveStateOption,
} from "./simulate/states";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  SystemNode,
  Terminal,
} from "./types";
import { terminalPolarity } from "./terminal-snap";

/** Inferred flow direction: the higher-ranked end feeds the lower. `fromNodeId`/
 *  `toNodeId` is only where the user dragged, not the flow. */
export type CableDirection = "forward" | "backward" | "none";

/** Ordinal only — a larger rank is more source-like on that rail. */
function rank(def: ComponentDefinition, term: Terminal): number | undefined {
  const polarity = terminalPolarity(term);
  const cat = def.category;
  const direction = term.direction;

  if (polarity === "positive") {
    if (cat === "solar" || cat === "source" || cat === "engine") return 5;
    if (cat === "charger" && direction === "output") return 4;
    if (cat === "distribution" && direction === "output") return 4;
    if (cat === "battery") return 3;
    if (cat === "distribution") return 2;
    if (cat === "charger" && direction === "input") return 1;
    if (cat === "load") return 0;
    return undefined;
  }

  if (polarity === "negative") {
    if (cat === "load") return 5;
    if (cat === "charger" && direction === "input") return 4;
    if (cat === "distribution" && direction === "output") return 4;
    if (cat === "distribution") return 2;
    if (cat === "battery") return 1;
    if (cat === "charger" && direction === "output") return 0;
    if (cat === "solar" || cat === "source" || cat === "engine") return 0;
    return undefined;
  }

  return undefined;
}

function findTerminal(def: ComponentDefinition, termId: string): Terminal | undefined {
  return def.terminals.find((t) => t.id === termId);
}

/** Ranks reachable from an endpoint. Both ends are kept: parallel sources merged
 *  at a junction tie on `max` and separate only on `min`. */
type RankRange = { min: number; max: number };

function aggregateThrough(
  node: SystemNode,
  originCableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: (id: string, custom?: ComponentDefinition) => ComponentDefinition | undefined,
  visited: Set<string>,
  simulate: SimulateContext | undefined,
  throughSiblings: boolean,
  /** Walk only cables on this exact terminal — a leaf's posts are not common. */
  restrictToTerminalId?: string,
): RankRange | undefined {
  if (visited.has(node.id)) return undefined;
  visited.add(node.id);

  let entryTermId: string | null = null;
  const origin = cables.find((c) => c.id === originCableId);
  if (origin) {
    if (origin.fromNodeId === node.id) entryTermId = origin.fromTerminalId;
    else if (origin.toNodeId === node.id) entryTermId = origin.toTerminalId;
  }
  let intraConnected: Set<string> | undefined;
  if (node.kind === "component" && entryTermId != null) {
    const c = node as ComponentNode;
    const def = defLookup(c.definitionId, c.customDefinition);
    if (def) {
      intraConnected = getActiveIntraLinkTargets(def, c.id, simulate, entryTermId);
    }
  }

  let acc: RankRange | undefined;
  for (const c of cables) {
    if (c.id === originCableId) continue;
    let nextNodeId: string | null = null;
    let nextTermId: string | null = null;
    let localTermId: string | null = null;
    if (c.fromNodeId === node.id) {
      localTermId = c.fromTerminalId;
      nextNodeId = c.toNodeId;
      nextTermId = c.toTerminalId;
    } else if (c.toNodeId === node.id) {
      localTermId = c.toTerminalId;
      nextNodeId = c.fromNodeId;
      nextTermId = c.fromTerminalId;
    }
    if (!nextNodeId || !nextTermId) continue;
    if (restrictToTerminalId != null && localTermId !== restrictToTerminalId) continue;
    if (intraConnected && !intraConnected.has(localTermId!)) continue;
    const r = endpointRank(
      nextNodeId,
      nextTermId,
      c.id,
      nodesById,
      cables,
      defLookup,
      visited,
      simulate,
      throughSiblings,
    );
    if (!r) continue;
    acc = acc
      ? { min: Math.min(acc.min, r.min), max: Math.max(acc.max, r.max) }
      : r;
  }
  return acc;
}

function simulateLeafRank(
  def: ComponentDefinition,
  term: Terminal,
  nodeId: string,
  simulate: SimulateContext | undefined,
): { rank?: number; suppress?: boolean } | undefined {
  if (!simulate) return undefined;
  const opt = getEffectiveStateOption(def, nodeId, simulate);
  if (!opt) return undefined;
  if (opt.ampOverride === 0) return { suppress: true };
  // Two more shapes mean "off". Falling through to the static table would paint
  // current out of a switched-off device.
  if (
    opt.ampMultiplier === 0 ||
    (opt.role == null && opt.ampOverride == null && opt.ampMultiplier == null)
  ) {
    return { suppress: true };
  }
  const polarity = terminalPolarity(term);
  if (polarity !== "positive" && polarity !== "negative") return undefined;
  // `role` names the *rail* the part acts on, so it may only re-rank that rail:
  // a converter's "output" is its BATT side, not the PV input feeding it.
  const hasInput = def.terminals.some((t) => t.direction === "input");
  const hasOutput = def.terminals.some((t) => t.direction === "output");
  if (hasInput && hasOutput) {
    const ownRail = opt.role === "output" ? "output" : "input";
    if (term.direction !== ownRail) return undefined;
  }
  if (opt.role === "output") return { rank: polarity === "positive" ? 5 : 0 };
  if (opt.role === "draw") return { rank: polarity === "positive" ? 0 : 5 };
  return undefined;
}

function endpointRank(
  nodeId: string,
  termId: string,
  originCableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: (id: string, custom?: ComponentDefinition) => ComponentDefinition | undefined,
  visited: Set<string>,
  simulate: SimulateContext | undefined,
  throughSiblings: boolean,
): RankRange | undefined {
  const node = nodesById[nodeId];
  if (!node) return undefined;

  if (node.kind === "component") {
    const c = node as ComponentNode;
    const def = defLookup(c.definitionId, c.customDefinition);
    if (isJunction(c, def)) {
      return aggregateThrough(
        node, originCableId, nodesById, cables, defLookup, visited, simulate, throughSiblings,
      );
    }
    if (!def) return undefined;

    if (isPassthroughComponent(def)) {
      // An open switch breaks the conducting path — don't propagate across it.
      if (simulate) {
        const opt = getEffectiveStateOption(def, c.id, simulate);
        if (opt?.ampOverride === 0) return undefined;
      }
      return aggregateThrough(
        node, originCableId, nodesById, cables, defLookup, visited, simulate, throughSiblings,
      );
    }

    const term = findTerminal(def, termId);
    if (!term) return undefined;
    const sim = simulateLeafRank(def, term, c.id, simulate);
    if (sim?.suppress) return undefined;
    const r = sim?.rank ?? rank(def, term);
    if (r == null) return undefined;
    const own: RankRange = { min: r, max: r };
    if (!throughSiblings || visited.has(c.id)) return own;
    // Same-terminal splice (a daisy-chained solar string). Tiebreak pass only —
    // in the primary rank it flips cables that already resolve. Don't pre-mark
    // the node visited: `aggregateThrough` does that, and the walk would stop.
    const sib = aggregateThrough(
      node, originCableId, nodesById, cables, defLookup, visited, simulate, throughSiblings, termId,
    );
    if (!sib) return own;
    return { min: Math.min(own.min, sib.min), max: Math.max(own.max, sib.max) };
  }

  return undefined;
}

export function cableDirection(
  cable: Cable,
  nodes: SystemNode[],
  cables: Cable[],
  defLookup: (id: string, custom?: ComponentDefinition) => ComponentDefinition | undefined,
  simulate?: SimulateContext,
): CableDirection {
  const nodesById = indexNodesById(nodes);
  const resolve = (throughSiblings: boolean): CableDirection => {
    const fromRank = endpointRank(
      cable.fromNodeId,
      cable.fromTerminalId,
      cable.id,
      nodesById,
      cables,
      defLookup,
      new Set(),
      simulate,
      throughSiblings,
    );
    const toRank = endpointRank(
      cable.toNodeId,
      cable.toTerminalId,
      cable.id,
      nodesById,
      cables,
      defLookup,
      new Set(),
      simulate,
      throughSiblings,
    );
    if (fromRank == null || toRank == null) return "none";
    if (fromRank.max > toRank.max) return "forward";
    if (fromRank.max < toRank.max) return "backward";
    if (fromRank.min > toRank.min) return "forward";
    if (fromRank.min < toRank.min) return "backward";
    return "none";
  };
  const direct = resolve(false);
  return direct === "none" ? resolve(true) : direct;
}
